// CalendarModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { localizer } from "@pages/Program/components/calendar/utils/localizer";
import { MilestoneDisplay } from "@shared/interfaces/ProgramFiles";
import CustomDropdown from "@shared/components/CustomDropdown";
import MilestonesModal from "@pages/Program/components/milestones/MilestonesModal";
import { useParams } from "react-router-dom";
import { useMilestone } from "@pages/Program/components/milestones/utils/useMilestone";
import Loader from "@shared/components/Loader";
import { endOfMonth, endOfYear, startOfMonth, startOfYear } from "date-fns";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
}

interface CalendarEvent {
  milestones: MilestoneDisplay[];
  title: string;
}

type ProgramProject = { id: string; name?: string | null };

const statusColors: Record<MilestoneDisplay["status"], string> = {
  on_track: "#10b981",
  behind: "#f59e0b",
  at_risk: "#ef4444",
};

const CalendarModal: React.FC<Props> = ({
  isOpen,
  onClose,
  startDate,
  endDate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"month" | "year">("month");
  const [view, setView] = useState<View>("month");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const [milestones, setMilestones] = useState<MilestoneDisplay[]>([]);
  const {
    fetchProgramMilestones,
    fetchProgramLinkedProjects,
    fetchProjectMilestones,
  } = useMilestone();

  const [selectedMilestones, setSelectedMilestones] = useState<CalendarEvent>(
    {} as CalendarEvent
  );

  const [loading, setLoading] = useState<boolean>(false);

  const months = Array.from({ length: 12 }).map((_, index) =>
    new Date(0, index).toLocaleString("default", { month: "long" })
  );

  const filteredEvents = useMemo(() => {
    return viewMode === "month"
      ? milestones
      : milestones.filter((e) => !projectFilter || e.name === projectFilter);
  }, [viewMode, projectFilter, milestones]);

  const calendarEvents = filteredEvents.map((e) => ({
    ...e,
    start: new Date(e.start_date),
    end: new Date(e.end_date),
  }));

  const handleShowMore = (events: MilestoneDisplay[], _date: Date) => {
    setSelectedMilestones({ title: "", milestones: events });
    setIsModalOpen(true);
  };

  const handleCloseShowMore = () => {
    setIsModalOpen(false);
    setSelectedMilestones({} as CalendarEvent);
  };

  const { programId } = useParams();

  // Helper: compute range based on viewMode/currentDate
  const computeRange = (mode: "month" | "year", base: Date) => {
    const start = mode === "year" ? startOfYear(base) : startOfMonth(base);
    const end = mode === "year" ? endOfYear(base) : endOfMonth(base);
    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  // Unified fetcher that respects projectFilter (when provided) and viewMode
  const fetchMilestones = async (
    mode: "month" | "year",
    opts?: { projectId?: string }
  ) => {
    if (!programId) return;
    try {
      setLoading(true);
      const { start_date, end_date } = computeRange(mode, currentDate);

      if (opts?.projectId && mode === "month") {
        const data = await fetchProjectMilestones(
          Number(programId),
          opts.projectId
        );
        setMilestones(data.milestones ?? []);
      } else {
        const data = await fetchProgramMilestones(Number(programId), {
          start_date,
          end_date,
        });
        setMilestones(data.milestones ?? []);
      }
    } catch (err) {
      console.error("Failed to fetch milestones:", err);
    } finally {
      setLoading(false);
    }
  };

  const [programProjectsLoading, setProgramProjectsLoading] =
    useState<boolean>(false);
  const [programProjects, setProgramProjects] = useState<ProgramProject[]>([]);

  const getProgramProjects = async (pid: number) => {
    try {
      setProgramProjectsLoading(true);
      const data = await fetchProgramLinkedProjects(pid);
      // Normalize IDs to string to avoid equality issues
      const normalized: ProgramProject[] = (data ?? []).map((p: any) => ({
        id: String(p.id ?? ""),
        name: p.name ?? "Unassigned",
      }));
      setProgramProjects(normalized);
    } catch (err) {
      console.error("Failed to fetch linked projects:", err);
    } finally {
      setProgramProjectsLoading(false);
    }
  };

  const handleMonthClickFromYear = (monthIndex: number) => {
    const updated = new Date(currentDate);
    updated.setMonth(monthIndex);
    setCurrentDate(updated);
    setViewMode("month");
  };

  const handleFilterTypeChange = async (val: string) => {
    setProjectFilter(val);
  };

  // Load projects once programId is available
  useEffect(() => {
    if (!programId) return;
    void getProgramProjects(Number(programId));
  }, [programId]);

  // Ensure we have projects when toggling view (and clear month data for fresh load)
  useEffect(() => {
    setMilestones([]);
    if (viewMode === "month" && programProjects.length === 0 && programId) {
      void getProgramProjects(Number(programId));
    }
    if (viewMode === "year") {
      // When switching to year view, clear the project filter
      setProjectFilter("");
    }
  }, [viewMode]);

  // Core change #1: On month navigation, respect projectFilter
  useEffect(() => {
    if (!isOpen) return;

    if (viewMode === "year") {
      void fetchMilestones("year");
      return;
    }

    // viewMode === "month"
    if (projectFilter) {
      // If a project is selected, fetch project milestones for the new month
      void fetchMilestones("month", { projectId: projectFilter });
    } else {
      // No filter → fetch program milestones for the month
      void fetchMilestones("month");
    }
  }, [currentDate, isOpen, viewMode, projectFilter]); // <-- keep projectFilter here so navigating after changing filter stays consistent

  // Core change #2: When filter changes in month view, fetch accordingly
  useEffect(() => {
    if (!isOpen || viewMode === "year") return;

    if (!projectFilter) {
      void fetchMilestones("month");
      return;
    }

    const exists = programProjects.some((p) => p.id === projectFilter);
    if (!exists) return;

    void fetchMilestones("month", { projectId: projectFilter });
  }, [projectFilter, viewMode, isOpen, programProjects]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] py-6 px-6 shadow-lg flex flex-col relative">
        <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200">
          <h2 className="text-lg 2xl:text-xl font-semibold">Calendar</h2>
          <button
            onClick={onClose}
            className="text-red-500 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition absolute top-2 right-2"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex-1 flex justify-between items-center">
            <button
              className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
              onClick={() => {
                const newDate = new Date(currentDate);
                if (viewMode === "month") {
                  newDate.setMonth(currentDate.getMonth() - 1);
                } else {
                  newDate.setFullYear(currentDate.getFullYear() - 1);
                }
                setCurrentDate(newDate);
              }}
              disabled={loading}
            >
              <img src="/back-arrow.svg" alt="" className="w-4 2xl:w-5 " />
            </button>
            <h2 className="text-base 2xl:text-lg font-semibold">
              {viewMode === "month"
                ? currentDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })
                : currentDate.getFullYear()}
            </h2>
            <button
              className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
              onClick={() => {
                const newDate = new Date(currentDate);
                if (viewMode === "month") {
                  newDate.setMonth(currentDate.getMonth() + 1);
                } else {
                  newDate.setFullYear(currentDate.getFullYear() + 1);
                }
                setCurrentDate(newDate);
              }}
              disabled={loading}
            >
              <img src="/forward-arrow.svg" alt="" className="w-4 2xl:w-5 " />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-72">
              <CustomDropdown
                value={projectFilter}
                onChange={handleFilterTypeChange}
                options={
                  viewMode === "month"
                    ? [
                        { label: "Filter by project", value: "" },
                        ...programProjects.map((group) => ({
                          label: group.name ?? "Unassigned",
                          value: group.id, // string
                        })),
                      ]
                    : [
                        { label: "Filter by task", value: "" },
                        ...[...new Set(milestones.map((e) => e.name))].map(
                          (title) => ({
                            label: title,
                            value: title,
                          })
                        ),
                      ]
                }
                placeholder={
                  viewMode === "month" ? "Filter by project" : "Filter by task"
                }
              />
            </div>

            <div className="w-28">
              <CustomDropdown
                value={viewMode}
                onChange={(v) => {
                  setLoading(true);
                  setViewMode(v as "month" | "year");
                }}
                options={[
                  { label: "Month", value: "month" },
                  { label: "Year", value: "year" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader color="#2462EB" size="lg" />
            </div>
          ) : viewMode === "month" ? (
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              defaultView="month"
              view={view}
              onView={setView}
              popup
              views={{
                month: true,
                day: false,
              }}
              style={{ height: "100%" }}
              components={{
                toolbar: () => null,
                event: ({ event }) => (
                  <div
                    className="text-xs font-medium truncate"
                    onClick={() => {
                      setSelectedMilestones({
                        milestones: [event],
                        title: "",
                      });
                      setIsModalOpen(true);
                    }}
                  >
                    {event.name}
                  </div>
                ),
              }}
              eventPropGetter={(event: MilestoneDisplay) => ({
                style: {
                  backgroundColor: statusColors[event.status],
                  borderRadius: "6px",
                  color: "#fff",
                  padding: "4px 8px",
                  fontSize: "12px",
                  border: "none",
                },
              })}
              onShowMore={handleShowMore}
            />
          ) : (
            <div className="grid grid-cols-4 gap-4 text-center ">
              {months.map((monthName, index) => {
                const start = new Date(currentDate.getFullYear(), index, 1);
                const end = new Date(currentDate.getFullYear(), index + 1, 0);

                const days: Date[] = [];
                for (
                  let d = new Date(start);
                  d <= end;
                  d.setDate(d.getDate() + 1)
                ) {
                  days.push(new Date(d.getTime()));
                }

                const offset = (start.getDay() + 6) % 7;

                return (
                  <div
                    key={index}
                    className="cursor-pointer hover:bg-gray-100 rounded-lg py-2 px-1"
                    onClick={() => handleMonthClickFromYear(index)}
                  >
                    <h4 className="font-semibold text-sm mb-3">{monthName}</h4>
                    <div className="grid grid-cols-7 text-xs text-gray-500 mb-1">
                      {"MTWTFSS".split("").map((d, idx) => (
                        <div key={`dow-${idx}`}>{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 text-sm text-gray-700">
                      {Array.from({ length: offset }).map((_, i) => (
                        <div key={`pad-${i}`}></div>
                      ))}
                      {days.map((day, i) => {
                        const event = projectFilter
                          ? milestones.find(
                              (e) =>
                                e.name === projectFilter &&
                                day.getTime() >=
                                  new Date(e.start_date).setHours(0, 0, 0, 0) &&
                                day.getTime() <=
                                  new Date(e.end_date).setHours(0, 0, 0, 0)
                            )
                          : undefined;
                        const color = event
                          ? statusColors[event.status]
                          : undefined;

                        return (
                          <div
                            key={i}
                            className={`relative px-1 py-1 cursor-pointer ${
                              color ? "text-white" : ""
                            }`}
                            style={{ backgroundColor: color }}
                            title={event?.name || ""}
                            onClick={(e) => {
                              if (event) {
                                setSelectedMilestones({
                                  title: event.name,
                                  milestones: [event],
                                });
                                setIsModalOpen(true);
                                e.stopPropagation();
                              }
                            }}
                          >
                            {day.getDate()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && selectedMilestones && startDate && endDate && (
        <MilestonesModal
          title={selectedMilestones.title}
          milestones={selectedMilestones.milestones}
          onClose={handleCloseShowMore}
          singleMilestone={selectedMilestones.milestones.length === 1}
        />
      )}
    </div>
  );
};

export default CalendarModal;
